// Bande-son d'une animation : musique d'ambiance en boucle (très basse) et
// jingles de l'intro (« JeRaisonne présente ») et du générique. Les sons sont
// choisis en base (bibliothèque `animation_audio`, migration 0669) ; le
// lecteur reçoit leurs URL.
//
// Volume par Web Audio (un `GainNode`) : iOS ignore le volume des balises
// audio. La musique passe par une balise <audio> branchée sur le graphe
// (pas de décodage complet d'un long morceau) ; les jingles, courts, sont
// décodés. Sans Web Audio (tests, vieux navigateurs) : balises <audio> et
// leur volume.

export interface SoundtrackSource {
  url: string;
  /** volume 0 … 1 (la musique de fond reste très basse) */
  gain: number;
}

export interface AnimationSoundtrack {
  music?: SoundtrackSource | null;
  jingleIntro?: SoundtrackSource | null;
  jingleOutro?: SoundtrackSource | null;
}

/** Montée de la musique au lancement, extinction au générique (ms). */
export const MUSIC_FADE_IN_MS = 1500;
export const MUSIC_FADE_OUT_MS = 2500;
/** Musique sous un jingle : part de son volume. */
export const MUSIC_DUCK = 0.35;
/** Durée prévue d'un jingle quand le fichier ne la donne pas (ms). */
export const JINGLE_MS = 2800;
/**
 * Durée maximale d'un jingle (ms), fondu de sortie compris : un « jingle » qui
 * est un morceau entier s'arrête. Miroir : MIX_JINGLE_MAX_MS (@jeraisonne/db/youtube).
 */
export const JINGLE_MAX_MS = 6000;
export const JINGLE_FADE_MS = 1000;

/** Un son chargé. */
export interface SoundHandle {
  play(opts: { loop: boolean; restart: boolean }): void;
  pause(): void;
  setGain(v: number, rampMs?: number): void;
  /** durée connue (ms) */
  durationMs(): number | null;
}

/** Sortie sonore (Web Audio ou balises <audio>) ; injectée dans les tests. */
export interface SoundBackend {
  /** DANS le geste de l'élève (« Lancer ») : iOS n'autorise le son qu'ainsi */
  unlock(): void;
  load(url: string, kind: 'stream' | 'buffer'): SoundHandle;
  setMaster(v: number): void;
  suspend(): void;
  resume(): void;
  dispose(): void;
}

type Ctx = AudioContext;

function audioContextClass(): (new () => Ctx) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: new () => Ctx;
    webkitAudioContext?: new () => Ctx;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Sortie par Web Audio ; `null` sans Web Audio ni <audio>. */
export function createSoundBackend(): SoundBackend | null {
  if (typeof window === 'undefined' || typeof Audio !== 'function') return null;
  const AC = audioContextClass();
  return AC ? new WebAudioBackend(AC) : new ElementBackend();
}

class ElementBackend implements SoundBackend {
  private els: HTMLAudioElement[] = [];
  private master = 1;
  private gains = new Map<HTMLAudioElement, number>();
  unlock() {
    for (const el of this.els) {
      if (!el.paused) continue;
      el.muted = true;
      void Promise.resolve(el.play())
        .then(() => {
          el.pause();
          el.currentTime = 0;
        })
        .catch(() => {})
        .finally(() => {
          el.muted = false;
        });
    }
  }
  load(url: string): SoundHandle {
    const el = new Audio();
    el.preload = 'auto';
    el.src = url;
    this.els.push(el);
    this.gains.set(el, 1);
    const apply = () => {
      el.volume = Math.max(0, Math.min(1, (this.gains.get(el) ?? 1) * this.master));
    };
    return {
      play: ({ loop, restart }) => {
        el.loop = loop;
        if (restart) el.currentTime = 0;
        apply();
        void Promise.resolve(el.play()).catch(() => {});
      },
      pause: () => el.pause(),
      setGain: (v) => {
        this.gains.set(el, v);
        apply();
      },
      durationMs: () => (Number.isFinite(el.duration) ? el.duration * 1000 : null),
    };
  }
  setMaster(v: number) {
    this.master = v;
    for (const el of this.els) el.volume = Math.max(0, Math.min(1, (this.gains.get(el) ?? 1) * v));
  }
  suspend() {
    for (const el of this.els) el.pause();
  }
  resume() {}
  dispose() {
    for (const el of this.els) {
      el.pause();
      el.removeAttribute('src');
    }
    this.els = [];
  }
}

class WebAudioBackend implements SoundBackend {
  private ctx: Ctx | null = null;
  private masterNode: GainNode | null = null;
  private masterValue = 1;
  private pending: (() => void)[] = [];
  private streams: HTMLAudioElement[] = [];
  private buffers = new Map<string, Promise<ArrayBuffer | null>>();

  constructor(private AC: new () => Ctx) {}

  private ensure(): Ctx | null {
    if (this.ctx) return this.ctx;
    try {
      this.ctx = new this.AC();
      this.masterNode = this.ctx.createGain();
      this.masterNode.gain.value = this.masterValue;
      this.masterNode.connect(this.ctx.destination);
      for (const f of this.pending.splice(0)) f();
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  unlock() {
    const ctx = this.ensure();
    if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
  }

  load(url: string, kind: 'stream' | 'buffer'): SoundHandle {
    return kind === 'stream' ? this.stream(url) : this.buffer(url);
  }

  private stream(url: string): SoundHandle {
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.preload = 'auto';
    el.src = url;
    this.streams.push(el);
    let gain: GainNode | null = null;
    let level = 1;
    const wire = () => {
      if (gain || !this.ctx || !this.masterNode) return;
      try {
        const src = this.ctx.createMediaElementSource(el);
        gain = this.ctx.createGain();
        gain.gain.value = level;
        src.connect(gain).connect(this.masterNode);
      } catch {
        gain = null;
      }
    };
    return {
      play: ({ loop, restart }) => {
        wire();
        el.loop = loop;
        if (restart) el.currentTime = 0;
        if (!gain) el.volume = Math.max(0, Math.min(1, level * this.masterValue));
        void Promise.resolve(el.play()).catch(() => {});
      },
      pause: () => el.pause(),
      setGain: (v, rampMs = 0) => {
        level = v;
        if (gain && this.ctx) ramp(gain.gain, v, rampMs, this.ctx);
        else el.volume = Math.max(0, Math.min(1, v * this.masterValue));
      },
      durationMs: () => (Number.isFinite(el.duration) ? el.duration * 1000 : null),
    };
  }

  private buffer(url: string): SoundHandle {
    if (!this.buffers.has(url))
      this.buffers.set(
        url,
        typeof fetch === 'function'
          ? fetch(url)
              .then((r) => (r.ok ? r.arrayBuffer() : null))
              .catch(() => null)
          : Promise.resolve(null),
      );
    let decoded: AudioBuffer | null = null;
    let decoding: Promise<void> | null = null;
    const decode = () => {
      if (decoded || decoding || !this.ctx) return decoding;
      const ctx = this.ctx;
      decoding = this.buffers
        .get(url)!
        .then((ab) => (ab ? ctx.decodeAudioData(ab.slice(0)) : null))
        .then((b) => {
          decoded = b;
        })
        .catch(() => {});
      return decoding;
    };
    let source: AudioBufferSourceNode | null = null;
    let gain: GainNode | null = null;
    let level = 1;
    const start = () => {
      const ctx = this.ctx;
      if (!ctx || !this.masterNode || !decoded) return;
      source?.stop();
      gain = ctx.createGain();
      gain.gain.value = level;
      source = ctx.createBufferSource();
      source.buffer = decoded;
      source.connect(gain).connect(this.masterNode);
      source.start();
    };
    this.pending.push(() => void decode());
    if (this.ctx) void decode();
    return {
      play: () => {
        if (decoded) start();
        else void decode()?.then(start);
      },
      pause: () => {
        source?.stop();
        source = null;
      },
      setGain: (v, rampMs = 0) => {
        level = v;
        if (gain && this.ctx) ramp(gain.gain, v, rampMs, this.ctx);
      },
      durationMs: () => (decoded ? decoded.duration * 1000 : null),
    };
  }

  setMaster(v: number) {
    this.masterValue = v;
    if (this.masterNode && this.ctx) ramp(this.masterNode.gain, v, 120, this.ctx);
    if (!this.masterNode) for (const el of this.streams) el.volume = Math.min(el.volume, v);
  }
  suspend() {
    for (const el of this.streams) el.pause();
    void this.ctx?.suspend().catch(() => {});
  }
  resume() {
    void this.ctx?.resume().catch(() => {});
  }
  dispose() {
    for (const el of this.streams) {
      el.pause();
      el.removeAttribute('src');
    }
    this.streams = [];
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }
}

function ramp(param: AudioParam, v: number, ms: number, ctx: Ctx) {
  const now = ctx.currentTime;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  if (ms <= 0) param.setValueAtTime(v, now);
  else param.linearRampToValueAtTime(v, now + ms / 1000);
}

export type SoundtrackScene = 'hook' | 'intro' | 'content' | 'outro';

/**
 * Bande-son pilotée par le lecteur : `begin` au lancement (dans le geste),
 * `scene` à chaque scène jouée, `pause` / `resume`, `finish` à la fin.
 * Timers injectables (tests).
 */
export class Soundtrack {
  private music: SoundHandle | null = null;
  private intro: SoundHandle | null = null;
  private outro: SoundHandle | null = null;
  private started = false;
  private paused = false;
  private musicOn = false;
  private duckUntil = 0;
  private duckTimer: ReturnType<typeof setTimeout> | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private jingleTimers: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private src: AnimationSoundtrack,
    private backend: SoundBackend,
    private clock: { now(): number } = { now: () => Date.now() },
  ) {
    if (src.music) this.music = backend.load(src.music.url, 'stream');
    if (src.jingleIntro) this.intro = backend.load(src.jingleIntro.url, 'buffer');
    if (src.jingleOutro) this.outro = backend.load(src.jingleOutro.url, 'buffer');
  }

  /** Lancement ou « Revoir » : DANS le geste. */
  begin(scene: SoundtrackScene) {
    this.backend.unlock();
    this.backend.resume();
    this.started = true;
    this.paused = false;
    this.clearTimers();
    if (this.music && this.src.music) {
      this.music.setGain(0);
      this.music.play({ loop: true, restart: true });
      this.musicOn = true;
      this.music.setGain(this.musicLevel(), MUSIC_FADE_IN_MS);
    }
    this.scene(scene);
  }

  /** Une scène commence (lecture en cours). */
  scene(kind: SoundtrackScene) {
    if (!this.started) return;
    if (kind === 'intro') this.jingle(this.intro, this.src.jingleIntro);
    if (kind === 'outro') {
      this.jingle(this.outro, this.src.jingleOutro);
      this.fadeOutMusic();
    } else if (this.music && this.src.music && !this.musicOn) {
      // retour en arrière depuis le générique : la musique revient
      this.music.play({ loop: true, restart: false });
      this.musicOn = true;
      this.music.setGain(this.musicLevel(), MUSIC_FADE_IN_MS);
    }
  }

  pause() {
    if (!this.started || this.paused) return;
    this.paused = true;
    this.backend.suspend();
  }

  resume() {
    if (!this.started || !this.paused) return;
    this.paused = false;
    this.backend.resume();
    if (this.musicOn) this.music?.play({ loop: true, restart: false });
  }

  /** Animation finie : la musique s'éteint. */
  finish() {
    if (!this.started) return;
    this.fadeOutMusic();
  }

  setMuted(muted: boolean) {
    this.backend.setMaster(muted ? 0 : 1);
  }

  dispose() {
    this.clearTimers();
    this.backend.dispose();
  }

  private musicLevel(): number {
    const g = this.src.music?.gain ?? 0;
    return this.clock.now() < this.duckUntil ? g * MUSIC_DUCK : g;
  }

  private jingle(h: SoundHandle | null, s: SoundtrackSource | null | undefined) {
    if (!h || !s) return;
    for (const t of this.jingleTimers) clearTimeout(t);
    this.jingleTimers = [];
    h.setGain(s.gain);
    h.play({ loop: false, restart: true });
    const known = h.durationMs();
    const ms = Math.min(known ?? JINGLE_MS, JINGLE_MAX_MS);
    // trop long (ou durée pas encore connue, fichier en cours de chargement) :
    // fondu puis arrêt à JINGLE_MAX_MS — sans effet sur un jingle déjà fini
    if (known === null || known > JINGLE_MAX_MS) {
      this.jingleTimers.push(
        setTimeout(() => h.setGain(0, JINGLE_FADE_MS), JINGLE_MAX_MS - JINGLE_FADE_MS),
        setTimeout(() => h.pause(), JINGLE_MAX_MS + 50),
      );
    }
    // la musique s'efface sous le jingle, puis revient
    this.duckUntil = this.clock.now() + ms;
    if (this.music && this.musicOn) {
      this.music.setGain((this.src.music?.gain ?? 0) * MUSIC_DUCK, 250);
      if (this.duckTimer) clearTimeout(this.duckTimer);
      this.duckTimer = setTimeout(() => {
        this.duckTimer = null;
        if (this.musicOn) this.music?.setGain(this.src.music?.gain ?? 0, 900);
      }, ms);
    }
  }

  private fadeOutMusic() {
    if (!this.music || !this.musicOn) return;
    this.musicOn = false;
    if (this.duckTimer) clearTimeout(this.duckTimer);
    this.duckTimer = null;
    this.music.setGain(0, MUSIC_FADE_OUT_MS);
    const m = this.music;
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.stopTimer = setTimeout(() => {
      this.stopTimer = null;
      if (!this.musicOn) m.pause();
    }, MUSIC_FADE_OUT_MS + 100);
  }

  private clearTimers() {
    for (const t of this.jingleTimers) clearTimeout(t);
    this.jingleTimers = [];
    if (this.duckTimer) clearTimeout(this.duckTimer);
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.duckTimer = null;
    this.stopTimer = null;
  }
}

/** Même bande-son ? (props comparées par valeur) */
export function soundtrackKey(s: AnimationSoundtrack | null | undefined): string {
  if (!s) return '';
  const k = (x: SoundtrackSource | null | undefined) => (x ? `${x.url}@${x.gain}` : '-');
  return `${k(s.music)}|${k(s.jingleIntro)}|${k(s.jingleOutro)}`;
}
